import React, { useMemo } from 'react';
import { ResolvedVideo } from './ResolvedVideo';

type Segment =
  | { type: 'html'; content: string }
  | { type: 'video'; mediaUuid: string; projectUuid: string; fileName: string | null };

// dangerouslySetInnerHTML renders static markup only — a video marker
// (<video data-media-uuid>, no src: see VideoEmbed.ts) needs live React to
// resolve a fresh playable url on every view (ResolvedVideo.tsx), which a
// single dangerouslySetInnerHTML blob can't do. This splits the saved HTML
// around any video markers, keeping everything else as one dangerouslySet
// block per run (so adjacent paragraphs/lists stay real siblings) and
// mounting a real <ResolvedVideo> for each video position.
const splitAroundVideoEmbeds = (html: string): Segment[] => {
  if (!html || typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html ? [{ type: 'html', content: html }] : [];
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const segments: Segment[] = [];
  let buffer = '';

  const flush = () => {
    if (buffer) {
      segments.push({ type: 'html', content: buffer });
      buffer = '';
    }
  };

  doc.body.childNodes.forEach((node) => {
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      (node as Element).tagName.toLowerCase() === 'video' &&
      (node as Element).hasAttribute('data-media-uuid')
    ) {
      flush();
      const el = node as Element;
      segments.push({
        type: 'video',
        mediaUuid: el.getAttribute('data-media-uuid') || '',
        projectUuid: el.getAttribute('data-project-uuid') || '',
        fileName: el.getAttribute('data-filename'),
      });
    } else {
      buffer += (node as Element).outerHTML ?? node.textContent ?? '';
    }
  });
  flush();

  return segments;
};

export const RenderSavedContent: React.FC<{ html: string; className?: string }> = ({ html, className }) => {
  const segments = useMemo(() => splitAroundVideoEmbeds(html), [html]);

  return (
    <div className={className}>
      {segments.map((segment, index) =>
        segment.type === 'video' ? (
          <ResolvedVideo
            key={index}
            projectUuid={segment.projectUuid}
            mediaUuid={segment.mediaUuid}
            fileName={segment.fileName}
          />
        ) : (
          <div key={index} dangerouslySetInnerHTML={{ __html: segment.content }} />
        ),
      )}
    </div>
  );
};

export default RenderSavedContent;
